// ============================================================
// palette.constants.ts
// CANONICAL colour PALETTE for the action-type colour system
// (firm house colours + per-user overrides + shipped defaults).
//
// PURGE-SAFE CONTRACT: every stripe/pill value below is a FULL,
// LITERAL Tailwind class string — never constructed or interpolated —
// so Tailwind's content scanner (tailwind.config.js globs src/**, with
// NO safelist) keeps them all in the build. Colour SCHEMES store only
// the TOKEN names (the keys of PALETTE), NEVER class strings, so no
// value coming from the DB / an API request can introduce an unscanned
// class that would render as nothing.
//
// KEEP IN SYNC: the backend duplicates ONLY the token LIST + the valid
// action-type keys for validation (backend/src/lib/colourScheme.ts).
// The backend does NOT need these classes — it validates tokens, the
// frontend renders them. If you add/remove/rename a token here, mirror
// the token name in that file.
//
// Adding a colour = add one literal entry here (+ its token in the
// backend list). Shades chosen to stay legible on both the light
// (#f2f8f5) and dark (#19211d) themes.
// ============================================================
export interface PaletteEntry {
  stripe: string // full `border-l-*` class — the row's left accent stripe
  pill: string   // full `bg-*/15 text-*` classes — the type pill
  swatch: string // full solid `bg-*` class — the picker swatch fill (Stage 3)
}

export const PALETTE: Record<string, PaletteEntry> = {
  // Brand green — the CURRENT letter/memo colour (casevo-mid token,
  // CSS-var backed so it re-themes in dark mode).
  green:   { stripe: 'border-l-casevo-mid',  pill: 'bg-casevo-mid/15 text-casevo-mid',   swatch: 'bg-casevo-mid' },
  // CURRENT call colour.
  blue:    { stripe: 'border-l-blue-400',    pill: 'bg-blue-400/15 text-blue-400',       swatch: 'bg-blue-400' },
  // CURRENT take_call colour.
  sky:     { stripe: 'border-l-sky-300',     pill: 'bg-sky-300/15 text-sky-300',         swatch: 'bg-sky-300' },
  teal:    { stripe: 'border-l-teal-400',    pill: 'bg-teal-400/15 text-teal-400',       swatch: 'bg-teal-400' },
  emerald: { stripe: 'border-l-emerald-400', pill: 'bg-emerald-400/15 text-emerald-400', swatch: 'bg-emerald-400' },
  cyan:    { stripe: 'border-l-cyan-400',    pill: 'bg-cyan-400/15 text-cyan-400',       swatch: 'bg-cyan-400' },
  indigo:  { stripe: 'border-l-indigo-400',  pill: 'bg-indigo-400/15 text-indigo-400',   swatch: 'bg-indigo-400' },
  violet:  { stripe: 'border-l-violet-400',  pill: 'bg-violet-400/15 text-violet-400',   swatch: 'bg-violet-400' },
  rose:    { stripe: 'border-l-rose-400',    pill: 'bg-rose-400/15 text-rose-400',       swatch: 'bg-rose-400' },
  amber:   { stripe: 'border-l-amber-400',   pill: 'bg-amber-400/15 text-amber-400',     swatch: 'bg-amber-400' },
  orange:  { stripe: 'border-l-orange-400',  pill: 'bg-orange-400/15 text-orange-400',   swatch: 'bg-orange-400' },
  slate:   { stripe: 'border-l-slate-400',   pill: 'bg-slate-400/15 text-slate-400',     swatch: 'bg-slate-400' },
}

// The ONLY values a colour scheme may store per action type. Derived
// from PALETTE so it can't drift from the class definitions above.
export const PALETTE_TOKENS: readonly string[] = Object.keys(PALETTE)

// Shipped-default scheme — actionType → token, reproducing the current
// hard-coded ACTION_COLOUR_MAP colours (call=blue, take_call=sky,
// letter/memo=green). This is the BOTTOM layer of the three-layer
// cascade (shipped ◄ firm ◄ user) that Stage 2 will resolve.
export const SHIPPED_DEFAULT_SCHEME: Record<string, string> = {
  call: 'blue',
  take_call: 'sky',
  letter: 'green',
  memo: 'green',
}

// Resolve an action type's colour through the three-layer cascade:
//   user.currentScheme[actionType]   (highest precedence)
//   ◄ firm.scheme[actionType]
//   ◄ SHIPPED_DEFAULT_SCHEME[actionType]
// → PALETTE[token] → { stripe, pill }.
//
// Returns null ONLY for action types that have NO shipped default
// (unknown / legacy '' — schemes can't reference those; they're
// rejected on write), so the caller keeps its own neutral fallback for
// those (TimelineRow's __fallback). If a resolved token is somehow
// absent from PALETTE (shouldn't happen — validated on write), falls
// back to the shipped default's palette entry so a row is NEVER
// rendered with an empty (purged) class.
//
// With BOTH schemes empty ({}), the cascade lands on
// SHIPPED_DEFAULT_SCHEME for every action type → identical to the
// pre-cascade ACTION_COLOUR_MAP colours (the Stage 2 correctness proof).
export function resolveActionColour(
  actionType: string,
  firmScheme: Record<string, string>,
  userScheme: Record<string, string>,
): PaletteEntry | null {
  const token =
    userScheme[actionType] ?? firmScheme[actionType] ?? SHIPPED_DEFAULT_SCHEME[actionType]
  if (!token) return null
  return PALETTE[token] ?? PALETTE[SHIPPED_DEFAULT_SCHEME[actionType]] ?? null
}
